package com.dotdrives.web.pages.drives

import com.dotdrives.web.ui.activateSurface
import com.dotdrives.design.DotAvatarPalette
import com.dotdrives.model.DrivePhase
import com.dotdrives.model.DriveSummary
import com.dotdrives.web.ui.DotBanner
import com.dotdrives.web.ui.DotEmptyState
import com.dotdrives.web.ui.DotPill
import com.dotdrives.web.ui.DotSkeleton
import react.FC
import react.Key
import react.Props
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.option
import react.dom.html.ReactHTML.select
import react.dom.html.ReactHTML.span
import web.cssom.ClassName
import web.html.ButtonType
import web.html.button as buttonType
import kotlin.time.Clock

external interface DriveBoardProps : Props {
    var loading: Boolean
    var phases: List<DrivePhase>
    var drives: List<DriveSummary>
    var phaseStats: List<com.dotdrives.model.DrivePhaseStats>
    var loadingPhases: Set<String>
    var onLoadMore: (String) -> Unit
    var query: String
    var hasActiveFilters: Boolean
    var error: String?
    var canWrite: Boolean
    var onRetry: () -> Unit
    var onOpen: (DriveSummary) -> Unit
    var onRequestMove: (DriveSummary, DrivePhase) -> Unit
    var onNewDrive: (DrivePhase) -> Unit
    var onClearFilters: () -> Unit
}

private val COLUMN_COLORS = listOf("#5B4AA8", "#4696EC", "#F26223", "#00A65A", "#B8491A")

val DriveBoard = FC<DriveBoardProps> { props ->
    props.error?.let { error ->
        DotBanner {
            message = error
            onRetry = props.onRetry
            className = "drives-inline-banner"
        }
    }

    val isFiltered = props.query.isNotBlank() || props.hasActiveFilters

    when {
        props.loading && props.drives.isEmpty() && props.error == null -> DriveBoardSkeleton {
            columnCount = maxOf(props.phases.size, 1)
        }
        props.phases.isEmpty() && !props.loading && props.error == null -> DriveBoardNoPhases()
        else -> {
            if (props.drives.isEmpty() && props.phases.isNotEmpty() && isFiltered && !props.loading && props.error == null) {
                div {
                    className = ClassName("drives-board-no-matches")
                    span { +"No drives match these filters." }
                    button {
                        type = ButtonType.buttonType
                        onClick = { props.onClearFilters() }
                        +"Clear filters"
                    }
                }
            }
            val columns = groupDrivesByPhase(props.phases, props.drives)
            div {
                className = ClassName("drives-board")
                div {
                    className = ClassName("drives-board-grid")
                    columns.forEachIndexed { index, column ->
                        DriveColumn {
                            key = column.phase.id.unsafeCast<Key>()
                            phase = column.phase
                            drives = column.drives
                            totalAskAmount = props.phaseStats.firstOrNull { it.phaseId == column.phase.id }?.askTotal ?: column.totalAskAmount
                            totalCount = props.phaseStats.firstOrNull { it.phaseId == column.phase.id }?.totalCount ?: column.drives.size.toLong()
                            loadingMore = column.phase.id in props.loadingPhases || props.loading
                            onLoadMore = { props.onLoadMore(column.phase.id) }
                            color = COLUMN_COLORS[index % COLUMN_COLORS.size]
                            canWrite = props.canWrite
                            allPhases = props.phases
                            onOpen = props.onOpen
                            onRequestMove = props.onRequestMove
                            onNewDrive = props.onNewDrive
                        }
                    }
                }
            }
        }
    }
}

private external interface DriveColumnProps : Props {
    var phase: DrivePhase
    var drives: List<DriveSummary>
    var totalCount: Long
    var loadingMore: Boolean
    var onLoadMore: () -> Unit
    var totalAskAmount: Double
    var color: String
    var canWrite: Boolean
    var allPhases: List<DrivePhase>
    var onNewDrive: (DrivePhase) -> Unit
    var onOpen: (DriveSummary) -> Unit
    var onRequestMove: (DriveSummary, DrivePhase) -> Unit
}

private val DriveColumn = FC<DriveColumnProps> { props ->
    val dotStyle = js("({})")
    dotStyle.backgroundColor = props.color

    div {
        className = ClassName("drives-column")
        div {
            className = ClassName("drives-column-head")
            div { className = ClassName("drives-column-dot"); style = dotStyle }
            div { className = ClassName("drives-column-name"); +props.phase.name }
            div { className = ClassName("drives-column-count"); +props.totalCount.toString() }
            div { className = ClassName("drives-column-total"); +formatMoneyCompact(props.totalAskAmount) }
            if (props.canWrite) {
                button {
                    type = ButtonType.buttonType
                    className = ClassName("drives-column-add")
                    ariaLabel = "Add drive to ${props.phase.name}"
                    title = "Add drive to ${props.phase.name}"
                    onClick = { props.onNewDrive(props.phase) }
                    +"+"
                }
            }
        }
        div {
            className = ClassName("drives-column-cards")
            if (props.drives.isEmpty()) {
                div {
                    className = ClassName("drives-column-empty")
                    +"No drives here"
                }
            }
            props.drives.forEach { drive ->
                DriveCard {
                    key = drive.id.unsafeCast<Key>()
                    this.drive = drive
                    columnColor = props.color
                    canWrite = props.canWrite
                    allPhases = props.allPhases
                    onOpen = props.onOpen
                    onRequestMove = props.onRequestMove
                }
            }
            if (props.drives.size < props.totalCount) {
                button {
                    type = ButtonType.buttonType
                    className = ClassName("drives-load-more")
                    disabled = props.loadingMore
                    ariaLabel = "Load more drives in ${props.phase.name}"
                    onClick = { props.onLoadMore() }
                    +if (props.loadingMore) "Loading…" else "Load more · ${props.drives.size} of ${props.totalCount}"
                }
            }

        }
    }
}

private external interface DriveCardProps : Props {
    var drive: DriveSummary
    var columnColor: String
    var canWrite: Boolean
    var allPhases: List<DrivePhase>
    var onOpen: (DriveSummary) -> Unit
    var onRequestMove: (DriveSummary, DrivePhase) -> Unit
}

/**
 * Deliberately a `div`, not a `button`: the move-phase `<select>` below needs to sit visually
 * over the card's top-right corner without ever being a *descendant* of the open-drive button.
 * `<button>` forbids interactive content in its content model (Firefox treats a nested control
 * as non-interactive; Chrome's handling of it isn't contractual), so the two are siblings here,
 * positioned with CSS instead of nested in the DOM. This card is the template eight more
 * modules copy their board markup from — getting the nesting wrong here ships it eight times.
 */
private val DriveCard = FC<DriveCardProps> { props ->
    val drive = props.drive
    val (avatarBackground, avatarText) = DotAvatarPalette.tintFor(drive.giverId)
    val avatarStyle = js("({})")
    avatarStyle.backgroundColor = avatarBackground
    avatarStyle.color = com.dotdrives.web.ui.accessibleAvatarText(avatarText)
    val age = driveAge(drive.phaseEnteredAt, Clock.System.now())
    val progressStyle = js("({})")
    progressStyle.width = "${taskProgressPercent(drive.tasksComplete, drive.tasksTotal)}%"
    progressStyle.backgroundColor = props.columnColor
    val otherPhases = props.allPhases
        .filter { it.id != drive.currentPhaseId }
        .sortedBy(DrivePhase::sequence)

    div {
        className = ClassName("drives-card dot-surface")
        onClick = { activateSurface(it) }

        button {
            type = ButtonType.buttonType
            className = ClassName("drives-card-open dot-surface-action")
            onClick = { props.onOpen(drive) }

            div {
                className = ClassName("drives-card-head")
                div { className = ClassName("drives-card-title"); +drive.title }
            }
            div {
                className = ClassName("drives-card-giver")
                +driveSubtitle(drive.giverType, drive.stewardName)
            }
            div {
                className = ClassName("drives-card-pills")
                span { className = ClassName("drives-card-target"); +(drive.target ?: "No target") }
                drive.tags.take(2).forEach { tag ->
                    DotPill {
                        key = tag.unsafeCast<Key>()
                        text = tag
                        tone = com.dotdrives.design.DotTone.Automation
                    }
                }
            }
            div {
                className = ClassName("drives-card-foot")
                div {
                    className = ClassName("drives-card-avatar-age")
                    div {
                        className = ClassName("drives-card-avatar")
                        style = avatarStyle
                        +driveInitials(drive.giverDisplayName)
                    }
                    span {
                        className = ClassName("drives-card-age ${age.toneClass}")
                        +age.label
                    }
                }
                div { className = ClassName("drives-card-amount"); +formatMoneyCompact(drive.askAmount) }
            }
            if (drive.tasksTotal > 0) {
                div {
                    className = ClassName("drives-card-progress")
                    div {
                        className = ClassName("drives-card-progress-track")
                        div { className = ClassName("drives-card-progress-fill"); style = progressStyle }
                    }
                    span {
                        className = ClassName("drives-card-progress-label")
                        +"${drive.tasksComplete}/${drive.tasksTotal}"
                    }
                }
            }
        }

        // Sibling of the open-drive button above, not a descendant of it -- see the class
        // docstring. Absolutely positioned over the card's top-right corner by CSS.
        if (props.canWrite && otherPhases.isNotEmpty()) {
            select {
                className = ClassName("drives-card-move-select")
                ariaLabel = "Move ${drive.title} to another phase"
                value = ""
                onChange = { event ->
                    val phaseId = event.currentTarget.value
                    otherPhases.firstOrNull { it.id == phaseId }?.let { target ->
                        props.onRequestMove(drive, target)
                    }
                }
                option { value = ""; +"Move →" }
                otherPhases.forEach { phase ->
                    option { key = phase.id.unsafeCast<Key>(); value = phase.id; +phase.name }
                }
            }
        }
    }
}

private external interface DriveBoardSkeletonProps : Props {
    var columnCount: Int
}

private val DriveBoardSkeleton = FC<DriveBoardSkeletonProps> { props ->
    div {
        className = ClassName("drives-board-skeleton")
        repeat(props.columnCount) {
            div {
                className = ClassName("drives-board-skeleton-column")
                DotSkeleton { height = "22px" }
                repeat(3) { DotSkeleton { height = "104px" } }
            }
        }
    }
}

/**
 * A process template with zero phases is a misconfiguration (Settings › Process, out of
 * this task's scope) — not the "no drives" empty state. Creating a drive against such a
 * template always fails server-side (`DriveClient.create` needs a first phase to seed
 * task instances from), so this state deliberately offers no CTA that would fail -- and,
 * since there is nothing actionable a board viewer can do about it, no primary action at all
 * rather than a button that does nothing.
 */
private val DriveBoardNoPhases = FC<Props> {
    div {
        className = ClassName("drives-empty-wrap")
        DotEmptyState {
            emoji = "⚠"
            title = "This process template has no phases"
            body = "Ask an admin to add phases to it from Settings before drives can start here."
            primaryAction = null
        }
    }
}
